import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls as ThreeOrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import {
  ArrowsClockwise,
  CaretDown,
  Camera,
  Cube,
  DotsThree,
  Eye,
  EyeSlash,
  FilePlus,
  Fire,
  House,
  Moon,
  Snowflake,
  Sun,
  Trash,
  Wrench,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { normalizePiece } from "@/components/CountertopDesigner";
import { expandMerged, mergeSeamPieces } from "@/lib/mergeSeams";
import {
  Cooktop,
  Dishwasher,
  Fridge,
  Hood,
  Range,
  SinkFixture,
} from "@/components/Appliances3D";
import { api, photoHref } from "@/lib/api";
import {
  cutoutRing,
  edgeFrame,
  insetPolygon,
  insidePoly,
  shapeFrom,
} from "@/lib/planGeometry";
import { plankFloorTexture, tileFloorTexture } from "@/lib/threeTextures";
import {
  ArrangeToolbar,
  DragLayer,
  SeamMarkers,
  SelectionMarker,
} from "@/components/Arrange3D";
import {
  arrangedPieces,
  nearestEdge,
  norm180,
  norm360,
  pieceCenter,
  pieceSeams,
  planToWorld,
  round2,
  seamIssues,
  sideLetter,
  snapPlacement,
  toDrawingPieces,
  usableSlab,
} from "@/lib/arrange3d";
import {
  CAB_COLORS,
  CAB_H,
  CAB_WOOD,
  Cabinet,
  PiecePlaced,
  UpperCabinet,
  configureEdgeStyles,
  edgeRole,
  finishOf,
  finishSlug,
  useCabinetMaterials,
} from "@/components/Cabinet3D";

export { CAB_COLORS };

const THICK = { "2cm": 0.79, "3cm": 1.18, "1.2cm": 0.47 };
const PROFILE_SEGS = 12;
const D2R = Math.PI / 180;
const WALL_H = 96;
const STONE_FALLBACK = "#a9a397";
const DOOR_STYLES = [
  ["shaker", "Shaker"],
  ["flat", "Flat"],
];
const HARDWARE = [
  ["pulls", "Pulls"],
  ["knobs", "Knobs"],
  ["none", "No handles"],
];
const COOKTOPS = [
  ["radiant", "Radiant"],
  ["induction", "Induction"],
];
const FLOORS = [
  ["auto", "Auto"],
  ["oak", "White oak"],
  ["plank", "Plank"],
  ["tile", "Tile"],
];
const isCooktop = (c) => /cooktop|range/i.test(c.kind || "");

// Older splits left a whole sink on piece A spilling past the seam: clip it to A and hand the
// rest to whichever unrotated neighbour it lands on, so each top cuts (and moves) its own part.
const splitSpillingCutouts = (pieces) => {
  const flat = pieces.map((p) => ({ ...p, cutouts: [...(p.cutouts || [])] }));
  flat.forEach((p) => {
    if ((Number(p.rot) || 0) !== 0) return;
    const L = Number(p.length_in) || 0;
    const W = Number(p.width_in) || 0;
    p.cutouts = p.cutouts.flatMap((c) => {
      if (c.seam_part || c.kind === "Radius corner") return [c];
      const x = Number(c.x) || 0;
      const y = Number(c.y) || 0;
      const w = Number(c.w) || 6;
      const h = Number(c.h) || 6;
      const overX = x + w > L + 0.01 ? "right" : x < -0.01 ? "left" : "";
      const overY = y + h > W + 0.01 ? "bottom" : y < -0.01 ? "top" : "";
      if (!overX && !overY) return [c];
      const cx0 = Math.max(0, x);
      const cx1 = Math.min(L, x + w);
      const cy0 = Math.max(0, y);
      const cy1 = Math.min(W, y + h);
      if (cx1 <= cx0 || cy1 <= cy0) return [c];
      // remainder in world coords → neighbour holding its centre
      const rx0 = overX === "right" ? cx1 : x;
      const rx1 = overX === "right" ? x + w : overX === "left" ? 0 : cx1;
      const ry0 = overY === "bottom" ? cy1 : y;
      const ry1 = overY === "bottom" ? y + h : overY === "top" ? 0 : cy1;
      const wx = (Number(p.x) || 0) + (rx0 + rx1) / 2;
      const wy = (Number(p.y) || 0) + (ry0 + ry1) / 2;
      const nb = flat.find(
        (q) =>
          q.id !== p.id &&
          (Number(q.rot) || 0) === 0 &&
          wx >= (Number(q.x) || 0) &&
          wx <= (Number(q.x) || 0) + (Number(q.length_in) || 0) &&
          wy >= (Number(q.y) || 0) &&
          wy <= (Number(q.y) || 0) + (Number(q.width_in) || 0),
      );
      if (nb) {
        nb.cutouts.push({
          ...c,
          id: `${c.id}-spill`,
          seam_part: true,
          part_of: c.id,
          x: (Number(p.x) || 0) + rx0 - (Number(nb.x) || 0),
          y: (Number(p.y) || 0) + ry0 - (Number(nb.y) || 0),
          w: overX ? rx1 - rx0 : w,
          h: overY ? ry1 - ry0 : h,
          seam_side: overX === "right" ? "left" : overX === "left" ? "right" : overY === "bottom" ? "top" : "bottom",
        });
      }
      return [{ ...c, x: cx0, y: cy0, w: cx1 - cx0, h: cy1 - cy0, seam_side: overX || overY }];
    });
  });
  return flat;
};

export const DEFAULT_SCENE = {
  cabinet: "White",
  mode: "light",
  cabinets: true,
  uppers: false,
  sinks: true,
  appliances: true,
  walls: false,
  door: "shaker",
  hardware: "pulls",
  cooktop: "radiant",
  floor: "auto",
  light: 1,
};

// polished stone: a touch of sheen over the photo without washing out the veins
const stoneMaterial = () =>
  new THREE.MeshPhysicalMaterial({
    color: STONE_FALLBACK,
    roughness: 0.3,
    metalness: 0,
    clearcoat: 0.22,
    clearcoatRoughness: 0.18,
    envMapIntensity: 0.4,
    side: THREE.DoubleSide,
  });

const withToken = photoHref;
const photoUrl = photoHref;
const defaultPhotoOf = (s) => (s?.photo_url ? withToken(s.photo_url) : "");

const arc = (cx, cz, r, a0, a1, segs = PROFILE_SEGS) =>
  Array.from({ length: segs + 1 }, (_, i) => {
    const a = a0 + ((a1 - a0) * i) / segs;
    return [cx + Math.cos(a) * r, cz + Math.sin(a) * r];
  });

// Edge cross-section in (n outward from the inset line, z up): inner top → outer → inner bottom.
// null means a square edge that needs no shaping.
export const profileSection = (name, m, t) => {
  const p = (name || "").toLowerCase();
  if (p.includes("bullnose") && (p.includes("half") || p.includes("demi"))) {
    const r = Math.min(0.8 * t, m);
    return [[0, t], ...arc(m - r, t - r, r, 90 * D2R, 0), [m, 0], [0, 0]];
  }
  if (p.includes("bullnose") || p.includes("full round")) {
    const r = t / 2;
    return [[0, t], ...arc(m - r, r, r, 90 * D2R, -90 * D2R), [0, 0]];
  }
  if (p.includes("ogee")) {
    const r = 0.45 * t;
    return [
      [0, t],
      ...arc(m - r, t, r, 180 * D2R, 270 * D2R),
      ...arc(m - r, 0.1 * t, r, 90 * D2R, 0),
      [m, 0],
      [0, 0],
    ];
  }
  if (p.includes("dupont")) {
    const r = 0.6 * t;
    return [
      [0, t],
      [m - r, t],
      [m - r, 0.7 * t],
      ...arc(m - r, 0.1 * t, r, 90 * D2R, 0),
      [m, 0],
      [0, 0],
    ];
  }
  if (p.includes("bevel") || p.includes("chamfer")) {
    const c = Math.min(0.5, 0.45 * t);
    return [
      [0, t],
      [m - c, t],
      [m, t - c],
      [m, 0],
      [0, 0],
    ];
  }
  if (p.includes("pencil") || p.includes("round")) {
    const r = 0.25 * t;
    return [
      [0, t],
      ...arc(m - r, t - r, r, 90 * D2R, 0),
      ...arc(m - r, r, r, 0, -90 * D2R),
      [0, 0],
    ];
  }
  return null;
};

// The shaped rim: each profiled edge's section swept between the inset line and the
// real edge, capped at both ends. Built straight in piece space (X = x, Y = up, Z = y).
const buildBands = (pts, inner, d, sections) => {
  const pos = [];
  const uv = [];
  const n = pts.length;
  const tri = (a, b, c) => {
    pos.push(...a.p, ...b.p, ...c.p);
    uv.push(...a.u, ...b.u, ...c.u);
  };
  for (let i = 0; i < n; i++) {
    const sec = sections[i];
    if (!sec) continue;
    const m = d[i];
    const A = pts[i];
    const B = pts[(i + 1) % n];
    const A2 = inner[i];
    const B2 = inner[(i + 1) % n];
    const f = edgeFrame(pts, i);
    const arcLen = [0];
    for (let k = 1; k < sec.length; k++)
      arcLen.push(
        arcLen[k - 1] +
          Math.hypot(sec[k][0] - sec[k - 1][0], sec[k][1] - sec[k - 1][1]),
      );
    const at = (k, s) => {
      const [nk, zk] = sec[k];
      const fr = nk / m;
      const ax = A2[0] + (A[0] - A2[0]) * fr;
      const ay = A2[1] + (A[1] - A2[1]) * fr;
      const bx = B2[0] + (B[0] - B2[0]) * fr;
      const by = B2[1] + (B[1] - B2[1]) * fr;
      // texture keeps flowing over the edge: slide outward by the arc length instead of n
      const ux = A2[0] + (B2[0] - A2[0]) * s + f.nx * arcLen[k];
      const uy = A2[1] + (B2[1] - A2[1]) * s + f.ny * arcLen[k];
      return { p: [ax + (bx - ax) * s, zk, ay + (by - ay) * s], u: [ux, -uy] };
    };
    for (let k = 0; k < sec.length - 1; k++) {
      const p00 = at(k, 0);
      const p01 = at(k, 1);
      const p10 = at(k + 1, 0);
      const p11 = at(k + 1, 1);
      tri(p00, p10, p11);
      tri(p00, p11, p01);
    }
    const caps = THREE.ShapeUtils.triangulateShape(
      sec.map(([a, b]) => new THREE.Vector2(a, b)),
      [],
    );
    [0, 1].forEach((s) =>
      caps.forEach(([i1, i2, i3]) => tri(at(i1, s), at(i2, s), at(i3, s))),
    );
  }
  if (!pos.length) return null;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.computeVertexNormals();
  return g;
};

// Core slab (inset where an edge is shaped) + the shaped rim; falls back to a plain
// extrusion when every edge is square or the outline is too small to inset.
export const buildStone = (piece, holes, t) => {
  const pts = piece.points;
  const m = Math.max(1, 1.25 * t);
  const sections = pts.map((_, i) => {
    const e = (piece.edges || [])[i] || {};
    return e.seam ? null : profileSection(e.profile, m, t);
  });
  const minEdge = Math.min(
    ...pts.map((a, i) => {
      const b = pts[(i + 1) % pts.length];
      return Math.hypot(b[0] - a[0], b[1] - a[1]);
    }),
  );
  const plain = () => ({
    core: new THREE.ExtrudeGeometry(shapeFrom(pts, holes), {
      depth: t,
      bevelEnabled: true,
      bevelThickness: 0.06,
      bevelSize: 0.06,
      bevelSegments: 2,
    }),
    band: null,
  });
  if (!sections.some(Boolean) || minEdge < 2.5 * m) return plain();
  const d = sections.map((s) => (s ? m : 0));
  const inner = insetPolygon(pts, d);
  if (!inner) return plain();
  return {
    core: new THREE.ExtrudeGeometry(shapeFrom(inner, holes), {
      depth: t,
      bevelEnabled: false,
    }),
    band: buildBands(pts, inner, d, sections),
  };
};

// tries each picture in turn, so a dead layout photo falls back to the stock photo
const useTexture = (urls) => {
  const [tex, setTex] = useState(null);
  const key = (urls || []).filter(Boolean).join("|");
  useEffect(() => {
    const list = key ? key.split("|") : [];
    if (!list.length) {
      setTex(null);
      return undefined;
    }
    let live = true;
    const configure = (t) => {
      t.wrapS = THREE.RepeatWrapping;
      t.wrapT = THREE.RepeatWrapping;
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 16;
      t.generateMipmaps = true;
      t.minFilter = THREE.LinearMipmapLinearFilter;
      t.magFilter = THREE.LinearFilter;
      return t;
    };
    const plain = (i) => {
      new THREE.TextureLoader().load(
        list[i],
        (t) => {
          if (!live) {
            t.dispose();
            return;
          }
          setTex(configure(t));
        },
        undefined,
        () => tryAt(i + 1),
      );
    };
    const tryAt = (i) => {
      if (!live) return;
      if (i >= list.length) {
        setTex(null);
        return;
      }
      // Re-draw the slab photo through a canvas with a mild contrast/saturation
      // lift so faint veins (e.g. light quartzites) read clearly in 3D & CAD.
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (!live) return;
        try {
          const maxSide = 4096;
          const s = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.max(1, Math.round(img.naturalWidth * s));
          const h = Math.max(1, Math.round(img.naturalHeight * s));
          const cv = document.createElement("canvas");
          cv.width = w;
          cv.height = h;
          const ctx = cv.getContext("2d");
          ctx.filter = "contrast(1.18) saturate(1.12) brightness(0.98)";
          ctx.drawImage(img, 0, 0, w, h);
          ctx.getImageData(0, 0, 1, 1); // throws if a cross-origin image tainted the canvas
          setTex(configure(new THREE.CanvasTexture(cv)));
        } catch {
          plain(i); // tainted or unsupported — fall back to the raw photo
        }
      };
      img.onerror = () => tryAt(i + 1);
      img.src = list[i];
    };
    tryAt(0);
    return () => {
      live = false;
    };
  }, [key]);
  return tex;
};

const firstPhoto = (slab) =>
  slab
    ? photoUrl((slab.photos || []).find((p) => !p.is_deleted)?.storage_path)
    : "";

// Which slab pictures a piece could wear (best first), and where on that slab it sits
const stoneFor = (piece, area, quote, slabStock, photoOf) => {
  const layout = quote.layout || {};
  const slabs = layout.slabs || [];
  const pl = (layout.placements || {})[piece.id];
  const color = (area.slab_color || quote.slab_color || "")
    .trim()
    .toLowerCase();
  const inv =
    slabStock.find(
      (s) =>
        (s.slab_color || "").trim().toLowerCase() === color &&
        (s.photos || []).some((p) => !p.is_deleted),
    ) ||
    slabStock.find(
      (s) =>
        color &&
        (s.slab_color || "").toLowerCase().includes(color) &&
        (s.photos || []).some((p) => !p.is_deleted),
    );
  const slab = pl ? slabs[pl.slab] : null;
  const slabInv = slab?.slab_id
    ? slabStock.find((s) => s.id === slab.slab_id)
    : null;
  const urls = [
    slab && photoOf(slab),
    firstPhoto(slabInv),
    ...slabs.map((s) => photoOf(s)),
    firstPhoto(inv),
  ].filter(Boolean);
  const W = Number(slab?.length_in || inv?.length_in) || 120;
  const H = Number(slab?.width_in || inv?.width_in) || 65;
  if (pl && slab) {
    // the layout board rotates about the origin and shifts back to the corner
    const r = ((((Number(pl.rot) || 0) % 360) + 360) % 360) * (Math.PI / 180);
    const m = piece.points.map(([x, y]) => [
      x * Math.cos(r) - y * Math.sin(r),
      x * Math.sin(r) + y * Math.cos(r),
    ]);
    const minX = Math.min(...m.map((p) => p[0]));
    const minY = Math.min(...m.map((p) => p[1]));
    return {
      urls,
      W,
      H,
      px: (Number(pl.x) || 0) - minX,
      py: (Number(pl.y) || 0) - minY,
      rot: (Number(pl.rot) || 0) * (Math.PI / 180),
    };
  }
  return {
    urls,
    W,
    H,
    px: (piece.x || 0) % W,
    py: (piece.y || 0) % H,
    rot: 0,
  };
};

const StonePiece = ({
  piece,
  thick,
  stone,
  tex,
  showSinks,
  cooktop,
  selected,
  interactive,
  onPointerDown,
}) => {
  const { gl } = useThree();
  const holes = useMemo(() => {
    const pts = piece.points || [];
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    const EPS = 0.05;
    const box = [Math.min(...xs) + EPS, Math.min(...ys) + EPS, Math.max(...xs) - EPS, Math.max(...ys) - EPS];
    return (piece.cutouts || [])
      .filter((c) => c.kind !== "Radius corner")
      .map((c) =>
        // a sink clipped at the seam (or spilling past the edge) must stay strictly inside the outline
        cutoutRing(c).map(([x, y]) => [
          Math.min(Math.max(x, box[0]), box[2]),
          Math.min(Math.max(y, box[1]), box[3]),
        ]),
      )
      .filter((ring) => {
        // keep a seam-clipped half-hole (its points sit on the outline) as long as it still
        // has area and its centre falls inside the piece — only drop fully-outside rings.
        const rxs = ring.map((p) => p[0]);
        const rys = ring.map((p) => p[1]);
        const rw = Math.max(...rxs) - Math.min(...rxs);
        const rh = Math.max(...rys) - Math.min(...rys);
        if (rw < 0.25 || rh < 0.25) return false;
        const cx = (Math.min(...rxs) + Math.max(...rxs)) / 2;
        const cy = (Math.min(...rys) + Math.max(...rys)) / 2;
        return insidePoly([cx, cy], pts);
      });
  }, [piece.cutouts, piece.points]);
  const built = useMemo(
    () => buildStone(piece, holes, thick),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [piece.points, piece.edges, holes, thick],
  );
  useEffect(
    () => () => {
      built.core.dispose();
      built.band?.dispose();
    },
    [built],
  );

  const { W: sW, H: sH, px: sPx, py: sPy, rot: sRot } = stone;
  const mat = useMemo(stoneMaterial, []);
  useEffect(() => () => mat.dispose(), [mat]);
  // texture space: the slab photo, with this piece's spot on the slab mapped onto it
  useEffect(() => {
    if (tex) {
      const c = Math.cos(sRot);
      const s = Math.sin(sRot);
      tex.matrixAutoUpdate = false;
      tex.matrix.set(
        c / sW,
        s / sW,
        sPx / sW,
        -s / sH,
        c / sH,
        1 - sPy / sH,
        0,
        0,
        1,
      );
      tex.needsUpdate = true;
    }
    mat.map = tex || null;
    mat.color.set(tex ? "#ffffff" : STONE_FALLBACK);
    mat.needsUpdate = true;
  }, [mat, tex, sW, sH, sPx, sPy, sRot]);
  useEffect(() => {
    mat.emissive.set("#6d5df6");
    mat.emissiveIntensity = selected ? 0.22 : 0;
    mat.needsUpdate = true;
  }, [mat, selected]);

  const cx = piece.length_in / 2;
  const cy = piece.width_in / 2;
  const rot = (-(Number(piece.rot) || 0) * Math.PI) / 180;

  return (
    <group
      position={[piece.x + cx, Number(piece.lift) || 0, piece.y + cy]}
      rotation={[0, rot, 0]}
      onPointerDown={
        onPointerDown
          ? (e) => {
              e.stopPropagation();
              onPointerDown(piece, e);
            }
          : undefined
      }
      onPointerOver={
        interactive
          ? (e) => {
              e.stopPropagation();
              gl.domElement.style.cursor = "grab";
            }
          : undefined
      }
      onPointerOut={
        interactive
          ? () => {
              gl.domElement.style.cursor = "";
            }
          : undefined
      }
    >
      <group position={[-cx, 0, -cy]}>
        <mesh
          geometry={built.core}
          material={mat}
          rotation={[-Math.PI / 2, 0, 0]}
          castShadow
          receiveShadow
        />
        {built.band && (
          <mesh geometry={built.band} material={mat} castShadow receiveShadow />
        )}
        {(piece.edges || []).map((e, i) => {
          if (!e.seam) return null;
          const f = edgeFrame(piece.points, i);
          return (
            <mesh
              key={`seam-${i}`}
              position={[f.mid[0], thick + 0.02, f.mid[1]]}
              rotation={[0, f.rotY, 0]}
            >
              <boxGeometry args={[f.len, 0.04, 0.14]} />
              <meshStandardMaterial color="#3b3b3f" roughness={0.6} />
            </mesh>
          );
        })}
        {showSinks &&
          (piece.cutouts || []).map((c) => {
            const w = Number(c.w) || 6;
            const h = Number(c.h) || 6;
            const kind = (c.kind || "").toLowerCase();
            const ccx = c.x + w / 2;
            const ccy = c.y + h / 2;
            const r = ((Number(c.rot) || 0) * -Math.PI) / 180;
            if (kind.includes("sink")) {
              return (
                <group key={c.id} position={[ccx, 0, ccy]} rotation={[0, r, 0]}>
                  <SinkFixture
                    w={w}
                    h={h}
                    thick={thick}
                    holes={c.seam_part ? 0 : Number(c.faucet_holes) || 0}
                    dropIn={kind.includes("drop")}
                    open={c.seam_side}
                  />
                </group>
              );
            }
            if (isCooktop(c)) {
              return (
                <group key={c.id} position={[ccx, 0, ccy]} rotation={[0, r, 0]}>
                  <Cooktop w={w} h={h} thick={thick} variant={cooktop} />
                </group>
              );
            }
            return null;
          })}
      </group>
    </group>
  );
};

// Backsplash / waterfall / mitre panel cut from the same slab picture
const Panel = ({ parent, panel, thick, tex, stone, onPointerDown }) => {
  const i = Number(panel.panel_edge) || 0;
  const f = edgeFrame(parent.points, i);
  const H = Math.max(...panel.points.map((p) => p[1]));
  const splash = panel.panel_kind === "backsplash";
  const t = splash ? 0.75 : thick;
  const cx = parent.length_in / 2;
  const cy = parent.width_in / 2;
  const rot = (-(Number(parent.rot) || 0) * Math.PI) / 180;
  const y = splash ? thick + H / 2 : thick - H / 2;
  const mat = useMemo(stoneMaterial, []);
  useEffect(() => () => mat.dispose(), [mat]);
  const t2 = useMemo(() => (tex ? tex.clone() : null), [tex]);
  useEffect(() => () => t2?.dispose(), [t2]);
  const { W: sW, H: sH, px: sPx, py: sPy } = stone;
  useEffect(() => {
    if (t2) {
      // box UVs run 0..1: show a len × H window of the slab beside the parent piece
      const u0 = ((((sPx + 1) / sW) % 1) + 1) % 1;
      const v0 = (((1 - (sPy + H + 2) / sH) % 1) + 1) % 1;
      t2.matrixAutoUpdate = false;
      t2.matrix.set(f.len / sW, 0, u0, 0, H / sH, v0, 0, 0, 1);
      t2.needsUpdate = true;
    }
    mat.map = t2;
    mat.color.set(t2 ? "#ffffff" : splash ? "#c4bdb1" : "#b3aca0");
    mat.needsUpdate = true;
  }, [mat, t2, sW, sH, sPx, sPy, f.len, H, splash]);
  return (
    <group
      position={[parent.x + cx, Number(parent.lift) || 0, parent.y + cy]}
      rotation={[0, rot, 0]}
    >
      <group position={[-cx, 0, -cy]}>
        <mesh
          position={[f.mid[0] + f.nx * (t / 2), y, f.mid[1] + f.ny * (t / 2)]}
          rotation={[0, f.rotY, 0]}
          material={mat}
          castShadow
          receiveShadow
          onPointerDown={
            onPointerDown
              ? (e) => {
                  e.stopPropagation();
                  onPointerDown(parent, e);
                }
              : undefined
          }
        >
          <boxGeometry args={[f.len, H, t]} />
        </mesh>
      </group>
    </group>
  );
};

// one slab picture per piece, shared by the stone and its panels
const PieceSet = ({
  piece,
  panels,
  thick,
  stone,
  showSinks,
  cooktop,
  selected,
  interactive,
  onPointerDown,
}) => {
  const tex = useTexture(stone.urls);
  // A backsplash drawn as an edge height (not a separate panel or upright piece) must still
  // rise as a stone panel in 3D/CAD, so every backsplash stands regardless of how it was drawn.
  const edgeSplashes = useMemo(
    () =>
      (piece.edges || [])
        .map((e, i) => ({ e, i }))
        .filter(
          ({ e, i }) =>
            Number(e?.backsplash_height_in) > 0 &&
            !panels.some(
              (pn) =>
                pn.panel_kind === "backsplash" && Number(pn.panel_edge) === i,
            ),
        )
        .map(({ e, i }) => ({
          id: `${piece.id}-bs-${i}`,
          panel_edge: i,
          panel_kind: "backsplash",
          points: [
            [0, 0],
            [0, Number(e.backsplash_height_in)],
          ],
        })),
    [piece.edges, piece.id, panels],
  );
  return (
    <>
      <StonePiece
        piece={piece}
        thick={thick}
        stone={stone}
        tex={tex}
        showSinks={showSinks}
        cooktop={cooktop}
        selected={selected}
        interactive={interactive}
        onPointerDown={onPointerDown}
      />
      {[...panels, ...edgeSplashes].map((pn) => (
        <Panel
          key={pn.id}
          parent={piece}
          panel={pn}
          thick={thick}
          tex={tex}
          stone={stone}
          onPointerDown={onPointerDown}
        />
      ))}
    </>
  );
};

// Which counter edge an upright panel stands on: an explicit `attach`, else the nearest edge.
const resolveEdge = (piece, flats) => {
  const parent = piece.attach?.piece_id
    ? flats.find((f) => f.id === piece.attach.piece_id)
    : null;
  if (parent && Array.isArray(parent.points) && parent.points.length > 1) {
    const n = parent.points.length;
    const e = Math.max(0, Math.min(n - 1, Number(piece.attach.edge) || 0));
    return { parent, e };
  }
  const pcx = (piece.x || 0) + (Number(piece.length_in) || 0) / 2;
  const pcz = (piece.y || 0) + (Number(piece.width_in) || 0) / 2;
  let best = null;
  flats.forEach((f) => {
    const pts = f.points || [];
    for (let i = 0; i < pts.length; i++) {
      const A = planToWorld(f, pts[i]);
      const B = planToWorld(f, pts[(i + 1) % pts.length]);
      const d = Math.hypot((A[0] + B[0]) / 2 - pcx, (A[1] + B[1]) / 2 - pcz);
      if (!best || d < best.d) best = { d, parent: f, e: i };
    }
  });
  return best ? { parent: best.parent, e: best.e } : null;
};

// A drawn piece set upright, standing on the counter edge it is snapped to. Backsplash rises
// off the counter top; waterfall drops from counter height down toward the floor.
const StandingPiece = ({
  piece,
  flats,
  thick,
  stone,
  selected,
  interactive,
  onPointerDown,
}) => {
  const { gl } = useThree();
  const tex = useTexture(stone.urls);
  const resolved = useMemo(() => resolveEdge(piece, flats), [piece, flats]);
  const waterfall = piece.stand === "waterfall";
  const H =
    Number(piece.width_in) > 0 ? Number(piece.width_in) : waterfall ? 34.5 : 4;
  const t = waterfall ? thick : 0.75;
  const mat = useMemo(stoneMaterial, []);
  useEffect(() => () => mat.dispose(), [mat]);
  const t2 = useMemo(() => (tex ? tex.clone() : null), [tex]);
  useEffect(() => () => t2?.dispose(), [t2]);
  const { W: sW, H: sH, px: sPx, py: sPy } = stone;
  const geom = useMemo(() => {
    if (!resolved) return null;
    const pts = resolved.parent.points;
    const A = planToWorld(resolved.parent, pts[resolved.e]);
    const B = planToWorld(resolved.parent, pts[(resolved.e + 1) % pts.length]);
    const dX = B[0] - A[0];
    const dZ = B[1] - A[1];
    const W = Math.hypot(dX, dZ) || 1;
    const alpha = Math.atan2(-dZ, dX);
    let mx = (A[0] + B[0]) / 2;
    let mz = (A[1] + B[1]) / 2;
    const pcx = (resolved.parent.x || 0) + (Number(resolved.parent.length_in) || 0) / 2;
    const pcz = (resolved.parent.y || 0) + (Number(resolved.parent.width_in) || 0) / 2;
    let nx = -dZ / W;
    let nz = dX / W;
    if ((mx - pcx) * nx + (mz - pcz) * nz < 0) {
      nx = -nx;
      nz = -nz;
    }
    mx += nx * (t / 2);
    mz += nz * (t / 2);
    return { W, alpha, mx, mz, lift: Number(resolved.parent.lift) || 0 };
  }, [resolved, t]);
  useEffect(() => {
    if (t2 && geom) {
      const u0 = (((sPx / sW) % 1) + 1) % 1;
      const v0 = (((1 - (sPy + H) / sH) % 1) + 1) % 1;
      t2.matrixAutoUpdate = false;
      t2.matrix.set(geom.W / sW, 0, u0, 0, H / sH, v0, 0, 0, 1);
      t2.needsUpdate = true;
    }
    mat.map = t2;
    mat.color.set(t2 ? "#ffffff" : STONE_FALLBACK);
    mat.needsUpdate = true;
  }, [mat, t2, sW, sH, sPx, sPy, H, geom]);
  useEffect(() => {
    mat.emissive.set("#6d5df6");
    mat.emissiveIntensity = selected ? 0.22 : 0;
    mat.needsUpdate = true;
  }, [mat, selected]);
  if (!geom) return null;
  const y = geom.lift + (waterfall ? thick - H / 2 : thick + H / 2);
  return (
    <group position={[geom.mx, y, geom.mz]} rotation={[0, geom.alpha, 0]}>
      <mesh
        material={mat}
        castShadow
        receiveShadow
        onPointerDown={
          onPointerDown
            ? (e) => {
                e.stopPropagation();
                onPointerDown(piece, e);
              }
            : undefined
        }
        onPointerOver={
          interactive
            ? (e) => {
                e.stopPropagation();
                gl.domElement.style.cursor = "grab";
              }
            : undefined
        }
        onPointerOut={
          interactive
            ? () => {
                gl.domElement.style.cursor = "";
              }
            : undefined
        }
      >
        <boxGeometry args={[geom.W, H, t]} />
      </mesh>
    </group>
  );
};

// A wall behind every edge that sits against one. Single-sided planes facing the room,
// so orbiting behind a wall looks straight through it instead of blocking the view.
const Walls = ({ pieces, dark }) => {
  const mat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: dark ? "#2b2b32" : "#ebe6dd",
        roughness: 0.92,
        side: THREE.FrontSide,
      }),
    [dark],
  );
  useEffect(() => () => mat.dispose(), [mat]);
  return pieces.map((piece) => {
    const cx = piece.length_in / 2;
    const cy = piece.width_in / 2;
    const rot = (-(Number(piece.rot) || 0) * Math.PI) / 180;
    return (
      <group
        key={`walls-${piece.id}`}
        position={[piece.x + cx, 0, piece.y + cy]}
        rotation={[0, rot, 0]}
      >
        <group position={[-cx, 0, -cy]}>
          {(piece.edges || []).map((e, i) => {
            if (edgeRole(e) !== "wall") return null;
            const f = edgeFrame(piece.points, i);
            const off = (Number(e.backsplash_height_in) > 0 ? 0.75 : 0) + 0.05;
            return (
              <mesh
                key={`wall-${i}`}
                position={[
                  f.mid[0] + f.nx * off,
                  -CAB_H + WALL_H / 2,
                  f.mid[1] + f.ny * off,
                ]}
                rotation={[0, f.rotY + (f.flipped ? 0 : Math.PI), 0]}
                material={mat}
                receiveShadow
              >
                <planeGeometry args={[f.len, WALL_H]} />
              </mesh>
            );
          })}
        </group>
      </group>
    );
  });
};

const Floor = ({ box, span, dark, kind }) => {
  const size = span * 8;
  const floor = kind === "auto" ? (dark ? "tile" : "plank") : kind;
  const tex = useMemo(() => {
    const t = (
      floor === "tile" ? tileFloorTexture() : plankFloorTexture(floor)
    ).clone();
    t.repeat.set(size / 48, size / 48);
    t.needsUpdate = true;
    return t;
  }, [floor, size]);
  useEffect(() => () => tex.dispose(), [tex]);
  const mat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: tex,
        roughness: floor === "tile" ? 0.32 : floor === "oak" ? 0.62 : 0.58,
        metalness: 0,
        envMapIntensity: floor === "tile" ? 0.5 : 0.35,
      }),
    [tex, floor],
  );
  useEffect(() => () => mat.dispose(), [mat]);
  return (
    <mesh
      position={[box.cx, -CAB_H - 0.01, box.cz]}
      rotation={[-Math.PI / 2, 0, 0]}
      material={mat}
      receiveShadow
    >
      <planeGeometry args={[size, size]} />
    </mesh>
  );
};

// Neutral tone mapping keeps the slab colors true to the photo; the room environment
// gives the polished stone and the hardware something to reflect.
const Studio = ({ dark, light = 1, cad = false }) => {
  const { gl, scene } = useThree();
  useEffect(() => {
    gl.toneMapping = THREE.NeutralToneMapping;
    gl.toneMappingExposure = cad ? 0.9 : dark ? 1.0 : 0.98;
    const pmrem = new THREE.PMREMGenerator(gl);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = env;
    scene.environmentIntensity = (cad ? 0.3 : dark ? 0.4 : 0.55) * light;
    return () => {
      scene.environment = null;
      env.dispose();
      pmrem.dispose();
    };
  }, [gl, scene, dark, light, cad]);
  return null;
};

// Renders the scene once at print resolution and hands back the picture. The pixel
// ratio is capped by what the GPU can hold so a 4K request never comes back blank.
const Capture = ({ api: ref }) => {
  const get = useThree((s) => s.get);
  useEffect(() => {
    ref.current = (width, type = "image/jpeg", quality = 0.92) => {
      const { gl, scene, camera, size } = get();
      const prev = gl.getPixelRatio();
      const maxTex = gl.capabilities.maxTextureSize || 4096;
      const cap = Math.floor(maxTex / Math.max(size.width, size.height));
      gl.setPixelRatio(Math.max(1, Math.min(cap, 8, width / size.width)));
      gl.render(scene, camera);
      const url = gl.domElement.toDataURL(type, quality);
      const outW = gl.domElement.width;
      gl.setPixelRatio(prev);
      gl.render(scene, camera);
      return { url, width: outW };
    };
    return () => {
      ref.current = null;
    };
  }, [get, ref]);
  return null;
};

const Cabinets = ({ pieces, finish, style, uppers, thick, onPointerDown }) => {
  const mats = useCabinetMaterials(finish);
  return (
    <>
      {pieces.map((p) => (
        <Cabinet
          key={`cab-${p.id}`}
          piece={p}
          mats={mats}
          style={style}
          onPointerDown={onPointerDown}
        />
      ))}
      {uppers &&
        pieces.map((p) => (
          <UpperCabinet
            key={`up-${p.id}`}
            piece={p}
            mats={mats}
            style={style}
            thick={thick}
          />
        ))}
    </>
  );
};

// Fridge / range placed against any edge the drawing marks as an appliance edge.
const FACING_CYCLE = ["out", "right", "in", "left"]; // 90° clockwise steps
const APPLIANCE_LABEL = { fridge: "Fridge", range: "Range / stove", dishwasher: "Dishwasher", hood: "Hood" };
const Appliances = ({ pieces, onSpin }) => {
  const { gl } = useThree();
  const press = useRef(null); // pointerdown position — a still press (no drag) spins
  return (
    <>
      {pieces.flatMap((p) => {
        const pts = p.points;
        if (!pts || pts.length < 3) return [];
        return (p.edges || []).map((e, i) => {
          if (!e?.appliance || i >= pts.length) return null;
          const f = edgeFrame(pts, i);
          const turn = { out: 0, left: Math.PI / 2, right: -Math.PI / 2, in: Math.PI };
          const rot = Math.atan2(f.nx, f.ny) + (turn[e.appliance_facing] || 0);
          return (
            <PiecePlaced key={`app-${p.id}-${i}`} piece={p}>
              <group
                position={[f.mid[0], -CAB_H, f.mid[1]]}
                rotation={[0, rot, 0]}
                onPointerDown={
                  onSpin
                    ? (ev) => {
                        ev.stopPropagation();
                        press.current = { x: ev.clientX, y: ev.clientY, key: `${p.id}-${i}` };
                      }
                    : undefined
                }
                onPointerUp={
                  onSpin
                    ? (ev) => {
                        const s = press.current;
                        press.current = null;
                        if (!s || s.key !== `${p.id}-${i}`) return;
                        if (Math.hypot(ev.clientX - s.x, ev.clientY - s.y) > 6) return; // was an orbit drag
                        ev.stopPropagation();
                        onSpin(p.id, i, ev.shiftKey ? 2 : 1);
                      }
                    : undefined
                }
                onPointerOver={
                  onSpin
                    ? (ev) => {
                        ev.stopPropagation();
                        gl.domElement.style.cursor = "pointer";
                      }
                    : undefined
                }
                onPointerOut={onSpin ? () => (gl.domElement.style.cursor = "") : undefined}
              >
                {e.appliance === "fridge" ? (
                  <Fridge
                    edgeLen={f.len}
                    width={Number(e.appliance_width) || 0}
                    height={e.appliance_height}
                  />
                ) : e.appliance === "dishwasher" ? (
                  <Dishwasher
                    edgeLen={f.len}
                    counterH={CAB_H}
                    width={Number(e.appliance_width) || 0}
                  />
                ) : e.appliance === "hood" ? (
                  <Hood edgeLen={f.len} width={Number(e.appliance_width) || 0} />
                ) : (
                  <Range
                    edgeLen={f.len}
                    counterH={CAB_H}
                    width={Number(e.appliance_width) || 0}
                  />
                )}
              </group>
            </PiecePlaced>
          );
        });
      })}
    </>
  );
};

const Orbit = ({ target, resetKey, controlsRef }) => {
  const { camera, gl } = useThree();
  const ref = useRef(null);
  useEffect(() => {
    const c = new ThreeOrbitControls(camera, gl.domElement);
    c.enableDamping = true;
    c.dampingFactor = 0.08;
    c.maxPolarAngle = Math.PI / 2 - 0.03;
    c.minDistance = 12;
    c.maxDistance = 1200;
    ref.current = c;
    if (controlsRef) controlsRef.current = c;
    return () => {
      c.dispose();
      if (controlsRef && controlsRef.current === c) controlsRef.current = null;
    };
  }, [camera, gl, controlsRef]);
  useEffect(() => {
    ref.current?.target.set(target[0], target[1], target[2]);
    ref.current?.update();
  }, [target, resetKey]);
  useFrame(() => ref.current?.update());
  return null;
};

const CameraFit = ({ box, resetKey }) => {
  const { camera } = useThree();
  useEffect(() => {
    const span = Math.max(box.w, box.d, 60);
    const dist = span * 1.15 + 50;
    camera.position.set(box.cx + dist * 0.55, dist * 0.55, box.cz + dist * 0.8);
    camera.near = 1;
    camera.far = 6000;
    camera.updateProjectionMatrix();
    camera.lookAt(box.cx, 0, box.cz);
  }, [camera, box.cx, box.cz, box.w, box.d, resetKey]);
  return null;
};

const Scene = ({
  pieces,
  panels,
  thick,
  scene,
  stones,
  box,
  cooktop,
  cad,
  selected,
  editable,
  seams = [],
  onPiecePointerDown,
  onRotateStart,
  onLiftStart,
  drag,
  controlsRef,
  onDragMove,
  onDragEnd,
  onSpinAppliance,
}) => {
  const dark = scene.mode === "dark";
  const finish = finishOf(scene.cabinet);
  const flats = pieces.filter((p) => p.orientation !== "vertical");
  const standing = pieces.filter((p) => p.orientation === "vertical");
  const selPiece = pieces.find((p) => p.id === selected) || null;
  const span = Math.max(box.w, box.d, 100);
  const light = Number(scene.light) > 0 ? Number(scene.light) : 1;
  const floor = FLOORS.some(([k]) => k === scene.floor) ? scene.floor : "auto";
  const style = {
    door: scene.door === "flat" ? "flat" : "shaker",
    hardware: HARDWARE.some(([k]) => k === scene.hardware)
      ? scene.hardware
      : "pulls",
    wood: CAB_WOOD[finish] || "",
  };
  // warm key light, cool fill, soft-edged shadows; `light` scales the whole room
  return (
    <>
      <Studio dark={dark} light={light} cad={cad} />
      <color attach="background" args={[cad ? "#0f172a" : dark ? "#111115" : "#f3efe7"]} />
      <hemisphereLight
        args={[
          dark ? "#6b6b80" : "#fff4e6",
          dark ? "#1a1a20" : "#cfc3b2",
          (dark ? 0.35 : 0.36) * light * (cad ? 0.65 : 1),
        ]}
      />
      <ambientLight
        color={dark ? "#ffffff" : "#fff1e3"}
        intensity={(dark ? 0.22 : 0.12) * light * (cad ? 0.65 : 1)}
      />
      <directionalLight
        color={dark ? "#f4e9dc" : "#fff0dc"}
        position={[box.cx + span, span * 1.4, box.cz + span * 0.6]}
        intensity={(dark ? 1.15 : 1.25) * light * (cad ? 0.58 : 1)}
        castShadow
        shadow-mapSize-width={4096}
        shadow-mapSize-height={4096}
        shadow-camera-near={10}
        shadow-camera-far={span * 6}
        shadow-camera-left={-span * 1.2}
        shadow-camera-right={span * 1.2}
        shadow-camera-top={span * 1.2}
        shadow-camera-bottom={-span * 1.2}
        shadow-bias={-0.0003}
        shadow-normalBias={0.03}
      />
      <directionalLight
        color="#dfe7f5"
        position={[box.cx - span, span * 0.8, box.cz - span]}
        intensity={0.45 * light * (cad ? 0.58 : 1)}
      />
      {!cad && <Floor box={box} span={span} dark={dark} kind={floor} />}
      {scene.walls === true && <Walls pieces={flats} dark={dark} />}
      {flats.map((p) => (
        <PieceSet
          key={p.id}
          piece={p}
          panels={panels.filter((pn) => pn.panel_of === p.id)}
          thick={thick}
          stone={stones[p.id]}
          showSinks={scene.sinks !== false}
          cooktop={cooktop}
          selected={p.id === selected}
          interactive={editable}
          onPointerDown={editable ? onPiecePointerDown : undefined}
        />
      ))}
      {standing.map((p) => (
        <StandingPiece
          key={p.id}
          piece={p}
          flats={flats}
          thick={thick}
          stone={stones[p.id]}
          selected={p.id === selected}
          interactive={editable}
          onPointerDown={editable ? onPiecePointerDown : undefined}
        />
      ))}
      {false && scene.seams !== false && seams.length > 0 && (
        <SeamMarkers seams={seams} thick={thick} />
      )}
      {selPiece && selPiece.orientation !== "vertical" && (
        <SelectionMarker
          piece={selPiece}
          thick={thick}
          editable={editable}
          onRotateStart={(e) => onRotateStart(selPiece, e)}
          onLiftStart={(e) => onLiftStart(selPiece, e)}
        />
      )}
      {editable && (
        <DragLayer
          drag={drag}
          controls={controlsRef}
          onMove={onDragMove}
          onEnd={onDragEnd}
        />
      )}
      {scene.cabinets !== false && (
        <Cabinets
          pieces={flats}
          finish={finish}
          style={style}
          uppers={scene.uppers === true}
          thick={thick}
          onPointerDown={editable ? onPiecePointerDown : undefined}
        />
      )}
      {scene.appliances !== false && <Appliances pieces={flats} onSpin={onSpinAppliance} />}
    </>
  );
};

const Chip = ({ on, onClick, children, testId, title }) => (
  <button
    type="button"
    data-testid={testId}
    title={title}
    onClick={onClick}
    className={`flex h-8 items-center gap-1.5 rounded-full border px-3 text-[10px] font-bold uppercase tracking-widest transition-all duration-150 ${
      on
        ? "border-indigo-400/60 bg-indigo-500 text-white"
        : "border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"
    }`}
  >
    {children}
  </button>
);

const Menu = ({ icon: Icon, label, testId, align = "left", children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-8 items-center gap-1.5 rounded-full border px-3 text-[10px] font-bold uppercase tracking-widest transition-all duration-150 ${
          open
            ? "border-indigo-400/60 bg-indigo-500/15 text-indigo-100"
            : "border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100"
        }`}
      >
        <Icon size={13} weight="bold" /> {label}
        <CaretDown
          size={10}
          weight="bold"
          className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          data-testid={`${testId}-panel`}
          className={`absolute z-30 mt-2 flex min-w-[240px] flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-3 shadow-2xl ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const MenuRow = ({ label, children }) => (
  <div className="space-y-1.5">
    <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    <div className="flex flex-wrap items-center gap-1.5">{children}</div>
  </div>
);

const EMPTY_OVER = {};

// Carry the Vein-Match installed orientation (quote.layout.install_rot) into the 3D room:
// turn each piece by its install angle and re-lay the area's pieces in a spaced row so the
// turned pieces (e.g. a mitred fireplace stood upright) don't overlap. Arrange overrides win.
const applyInstallTurn = (list, installRot) => {
  if (!installRot || !list.some((p) => installRot[p.id])) return list;
  const GAP = 6;
  let cursor = 0;
  return list.map((p) => {
    const ir = installRot[p.id] || 0;
    const rot = ((((Number(p.rot) || 0) + ir) % 360) + 360) % 360;
    const L = Number(p.length_in) || 0;
    const W = Number(p.width_in) || 0;
    const a = (rot * Math.PI) / 180;
    const rw = Math.abs(L * Math.cos(a)) + Math.abs(W * Math.sin(a));
    const rh = Math.abs(L * Math.sin(a)) + Math.abs(W * Math.cos(a));
    const cx = cursor + rw / 2;
    const cy = rh / 2;
    cursor += rw + GAP;
    return { ...p, rot, x: Math.round((cx - L / 2) * 100) / 100, y: Math.round((cy - W / 2) * 100) / 100 };
  });
};

export const Countertop3D = ({
  quote,
  slabStock = [],
  scene: sceneIn,
  onSceneChange,
  height = "calc(100vh - 140px)",
  photoOf = defaultPhotoOf,
  portalShown,
  onPortalShown,
  onHero,
  onArrangeChange,
  onApplyToDrawing,
  edgeStyles,
  cad = false,
  toolbar = true,
}) => {
  const scene = { ...DEFAULT_SCENE, ...(sceneIn || {}) };
  // the studio hands over the shop's edge styles so "unfinished" means the same here as
  // on the Draw tab; geometry is keyed on this so cabinets rebuild when styles arrive
  const styleKey = (edgeStyles || [])
    .map((s) => `${s.name}:${s.shape}`)
    .join("|");
  configureEdgeStyles(edgeStyles);
  const areas = (quote.areas || []).filter((a) =>
    (a.pieces || []).some((p) => !p.panel_kind && (p.points || []).length > 2),
  );
  const [areaId, setAreaId] = useState(areas[0]?.id || "");
  const [resetKey, setResetKey] = useState(0);
  const wrapRef = useRef(null);
  const area = areas.find((a) => a.id === areaId) || areas[0] || null;

  const basePieces = useMemo(
    () =>
      applyInstallTurn(
        mergeSeamPieces(
          (area?.pieces || [])
            .filter((p) => !p.panel_kind && (p.points || []).length > 2)
            .map(normalizePiece),
        ),
        quote?.layout?.install_rot || {},
      ),
    [area, quote?.layout?.install_rot],
  );
  const basePanels = useMemo(
    () => (area?.pieces || []).filter((p) => p.panel_kind && p.points?.length),
    [area],
  );
  const thick = THICK[quote.thickness] || 1.18;

  // Appliance edges in this area, so the fridge / range can be spun from the 3D view
  const appliances = useMemo(
    () =>
      basePieces.flatMap((piece) =>
        (piece.edges || [])
          .map((edge, idx) => ({ piece, idx, edge }))
          .filter(({ edge, idx }) => edge?.appliance && idx < (piece.points || []).length),
      ),
    [basePieces],
  );
  const spinAppliance = (pieceId, idx, quarterTurns) => {
    if (!area || !onApplyToDrawing) return;
    onApplyToDrawing(
      area.id,
      (area.pieces || []).map((p) => {
        if (p.id !== pieceId) return p;
        const edges = (p.edges || []).map((e, i) => {
          if (i !== idx) return e;
          const cur = Math.max(0, FACING_CYCLE.indexOf(e.appliance_facing || "out"));
          return { ...e, appliance_facing: FACING_CYCLE[(cur + quarterTurns) % 4] };
        });
        return { ...p, edges };
      }),
    );
  };

  // 3D arrangement layered over the drawing (quote.design.arrange[areaId][pieceId])
  const editable = !!onArrangeChange;
  const arrange = quote.design?.arrange || EMPTY_OVER;
  const areaOver = (area && arrange[area.id]) || EMPTY_OVER;
  const { pieces, panels } = useMemo(
    () => arrangedPieces(basePieces, basePanels, areaOver),
    [basePieces, basePanels, areaOver],
  );
  const stones = useMemo(
    () =>
      Object.fromEntries(
        basePieces.map((p) => [
          p.id,
          stoneFor(p, area, quote, slabStock, photoOf),
        ]),
      ),
    [basePieces, area, quote, slabStock, photoOf],
  );
  const [selected, setSelected] = useState(null);
  const [snapMode, setSnapMode] = useState("grid");
  const [drag, setDrag] = useState(null);
  const [connected, setConnected] = useState(null);
  const controlsRef = useRef(null);
  const piecesRef = useRef(pieces);
  piecesRef.current = pieces;

  // camera framing only re-fits on area change / reset, not while a piece is dragged
  const box = useMemo(() => {
    const list = piecesRef.current;
    if (!list.length) return { cx: 48, cz: 24, w: 96, d: 48 };
    const xs = list.flatMap((p) => [p.x, p.x + p.length_in]);
    const zs = list.flatMap((p) => [p.y, p.y + p.width_in]);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    const z0 = Math.min(...zs);
    const z1 = Math.max(...zs);
    return { cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, w: x1 - x0, d: z1 - z0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area?.id, resetKey, pieces.length]);

  const set = (patch) => onSceneChange?.({ ...scene, ...patch });
  const hidden = scene.hidden || [];
  const togglePiece = (pid) =>
    set({
      hidden: hidden.includes(pid)
        ? hidden.filter((x) => x !== pid)
        : [...hidden, pid],
    });
  const visiblePieces = useMemo(
    () => splitSpillingCutouts(pieces.filter((p) => !hidden.includes(p.id))),
    [pieces, hidden],
  );
  // joints between the tops as placed right now (arrangement included) — derived, not stored
  const seams = useMemo(() => {
    const byName = Object.fromEntries(pieces.map((p, i) => [p.id, p.label || `Piece ${i + 1}`]));
    const byId = Object.fromEntries(pieces.map((p) => [p.id, p]));
    const slab = usableSlab(quote, area, slabStock);
    return pieceSeams(visiblePieces).map((s) => ({
      ...s,
      label: `${byName[s.a]} ↔ ${byName[s.b]}`,
      issues: seamIssues(s, byId, slab),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieces, hidden, slabStock, quote.layout, quote.slab_color]);

  const patchOver = (pid, patch) => {
    if (!area || !onArrangeChange) return;
    onArrangeChange({
      ...arrange,
      [area.id]: {
        ...areaOver,
        [pid]: { ...(areaOver[pid] || {}), ...patch },
      },
    });
  };
  const clearOver = (pid) => {
    if (!area || !onArrangeChange) return;
    const next = { ...areaOver };
    delete next[pid];
    onArrangeChange({ ...arrange, [area.id]: next });
  };
  const clearArea = () => {
    if (!area || !onArrangeChange) return;
    const next = { ...arrange };
    delete next[area.id];
    onArrangeChange(next);
  };
  const hasOverrides = Object.keys(areaOver).length > 0;
  const selPiece = pieces.find((p) => p.id === selected) || null;
  const nameOf = (pid) => {
    const i = pieces.findIndex((p) => p.id === pid);
    return pieces[i]?.label || `Piece ${i + 1}`;
  };
  const rotateBy = (d) =>
    selPiece &&
    patchOver(selPiece.id, {
      rot: norm360((Number(selPiece.rot) || 0) + d),
    });
  const setRot = (v) =>
    selPiece && patchOver(selPiece.id, { rot: norm360(Number(v) || 0) });
  const flip = (axis) => {
    if (!selPiece) return;
    const key = axis === "h" ? "flip_h" : "flip_v";
    patchOver(selPiece.id, { [key]: !areaOver[selPiece.id]?.[key] });
  };
  const nudge = (dx, dy) =>
    selPiece &&
    selPiece.orientation !== "vertical" &&
    patchOver(selPiece.id, {
      x: round2((selPiece.x || 0) + dx),
      y: round2((selPiece.y || 0) + dy),
    });
  const liftBy = (d) =>
    selPiece &&
    selPiece.orientation !== "vertical" &&
    patchOver(selPiece.id, {
      lift: Math.max(0, round2((Number(selPiece.lift) || 0) + d)),
    });
  const setLift = (v) =>
    selPiece &&
    patchOver(selPiece.id, { lift: Math.max(0, round2(Number(v) || 0)) });

  const onPiecePointerDown = (p, e) => {
    setSelected(p.id);
    setConnected(null);
    if (!editable) return;
    if (controlsRef.current) controlsRef.current.enabled = false;
    if (p.orientation === "vertical") {
      setDrag({ kind: "attach", id: p.id, planeY: thick });
      return;
    }
    setDrag({
      kind: "move",
      id: p.id,
      planeY: e.point.y,
      offX: e.point.x - (p.x || 0),
      offZ: e.point.z - (p.y || 0),
    });
  };
  const onRotateStart = (p, e) => {
    if (controlsRef.current) controlsRef.current.enabled = false;
    const [cx, cz] = pieceCenter(p);
    setDrag({
      kind: "rotate",
      id: p.id,
      planeY: e.point.y,
      cx,
      cz,
      startRot: Number(p.rot) || 0,
      startAng: (Math.atan2(e.point.z - cz, e.point.x - cx) * 180) / Math.PI,
    });
  };
  const onLiftStart = (p, e) => {
    if (controlsRef.current) controlsRef.current.enabled = false;
    setDrag({
      kind: "lift",
      id: p.id,
      planeY: e.point.y,
      startLift: Number(p.lift) || 0,
      startY: e.nativeEvent?.clientY ?? e.clientY ?? 0,
    });
  };
  const onDragMove = (hx, hz, ev, inchPerPx) => {
    if (!drag) return;
    const list = piecesRef.current;
    const p = list.find((q) => q.id === drag.id);
    if (!p) return;
    const others = list.filter(
      (o) => o.id !== p.id && o.orientation !== "vertical" && !hidden.includes(o.id),
    );
    if (drag.kind === "lift") {
      const dy = (drag.startY - (ev?.clientY ?? drag.startY)) * (inchPerPx || 0.2);
      let lift = drag.startLift + dy;
      if (snapMode === "grid") lift = Math.round(lift);
      patchOver(p.id, { lift: Math.max(0, round2(lift)) });
      return;
    }
    if (!Number.isFinite(hx) || !Number.isFinite(hz)) return;
    if (drag.kind === "move") {
      const s = snapPlacement(
        { ...p, x: hx - drag.offX, y: hz - drag.offZ },
        others,
        snapMode,
      );
      setConnected(s.connected ? nameOf(s.connected) : null);
      patchOver(p.id, { x: s.x, y: s.y, rot: s.rot });
    } else if (drag.kind === "rotate") {
      const ang = (Math.atan2(hz - drag.cz, hx - drag.cx) * 180) / Math.PI;
      let rot = drag.startRot + norm180(ang - drag.startAng);
      if (snapMode === "grid") rot = Math.round(rot / 5) * 5;
      const s = snapPlacement({ ...p, rot }, others, snapMode, {
        translate: false,
      });
      patchOver(p.id, { rot: norm360(s.rot) });
    } else if (drag.kind === "attach") {
      const hit = nearestEdge([hx, hz], others, 14);
      if (hit) {
        patchOver(p.id, { attach: { piece_id: hit.piece_id, edge: hit.edge } });
        setConnected(`${nameOf(hit.piece_id)} · side ${sideLetter(hit.edge)}`);
      }
    }
  };
  const onDragEnd = () => setDrag(null);

  const applyToDrawing = () => {
    if (!area || !onApplyToDrawing) return;
    const halves = expandMerged(pieces);
    const overX = { ...areaOver };
    pieces.forEach((p) => (p.merged_from || []).forEach((h) => { if (areaOver[p.id]) overX[h.id] = areaOver[p.id]; }));
    onApplyToDrawing(
      area.id,
      toDrawingPieces(area.pieces || [], halves, panels, overX),
    );
    clearArea();
    toast.success("3D arrangement applied to the drawing");
  };

  useEffect(() => {
    if (!editable || !selPiece) return undefined;
    const h = (e) => {
      if (/^(input|textarea|select)$/i.test(e.target?.tagName || "")) return;
      const step = e.shiftKey ? 12 : 1;
      const map = {
        ArrowLeft: () => nudge(-step, 0),
        ArrowRight: () => nudge(step, 0),
        ArrowUp: () => nudge(0, -step),
        ArrowDown: () => nudge(0, step),
        PageUp: () => liftBy(e.shiftKey ? 6 : 1),
        PageDown: () => liftBy(e.shiftKey ? -6 : -1),
        r: () => rotateBy(15),
        R: () => rotateBy(-15),
        f: () => flip("h"),
        F: () => flip("v"),
        Escape: () => setSelected(null),
      };
      const fn = map[e.key];
      if (!fn) return;
      e.preventDefault();
      fn();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const cabinetName = finishOf(scene.cabinet);
  const cooktop = COOKTOPS.some(([k]) => k === scene.cooktop)
    ? scene.cooktop
    : "radiant";
  const hasCooktop = visiblePieces.some((p) =>
    (p.cutouts || []).some(isCooktop),
  );
  const floor = FLOORS.some(([k]) => k === scene.floor) ? scene.floor : "auto";
  const light = Number(scene.light) > 0 ? Number(scene.light) : 1;
  const captureRef = useRef(null);
  const [heroBusy, setHeroBusy] = useState(false);
  const hero = quote.design?.hero;

  const snapshot = (width = 2400, tag = "") => {
    const shot = captureRef.current?.(width, "image/png");
    if (!shot?.url) return;
    const a = document.createElement("a");
    a.href = shot.url;
    a.download = `${quote.code || "design"}-3d${tag}.png`;
    a.click();
    toast.success(`Picture saved at ${shot.width}px wide`);
  };

  const saveHero = async () => {
    const image = captureRef.current?.(2000)?.url;
    if (!image || !quote.id) return;
    setHeroBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/hero`, { image });
      onHero?.(data.hero);
      toast.success("Picture added to the quote document");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the picture");
    } finally {
      setHeroBusy(false);
    }
  };

  const removeHero = async () => {
    setHeroBusy(true);
    try {
      await api.delete(`/quotes/${quote.id}/hero`);
      onHero?.(null);
      toast.success("Picture removed from the quote document");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove the picture");
    } finally {
      setHeroBusy(false);
    }
  };

  return (
    <div
      data-testid="countertop-3d"
      className="flex h-full flex-col overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950"
      style={{ height: toolbar ? "auto" : "100%" }}
    >
      {toolbar && (
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800 bg-zinc-900/70 px-3 py-2">
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          <Cube size={13} weight="bold" className="text-indigo-300" /> 3D view
        </span>
        {areas.length > 1 && (
          <div className="flex items-center gap-1" data-testid="scene-areas">
            {areas.map((a) => (
              <Chip
                key={a.id}
                on={a.id === area?.id}
                onClick={() => setAreaId(a.id)}
                testId={`scene-area-${a.id}`}
              >
                {a.name || "Area"}
              </Chip>
            ))}
          </div>
        )}
        <span className="mx-1 hidden h-5 w-px bg-zinc-800 sm:block" />

        {onApplyToDrawing && area && appliances.length > 0 && (
          <Menu icon={Snowflake} label="Appliances" testId="scene-menu-appliances">
            {appliances.map(({ piece, idx, edge }) => {
              const label = APPLIANCE_LABEL[edge.appliance] || edge.appliance;
              const facing = edge.appliance_facing || "out";
              return (
                <MenuRow
                  key={`${piece.id}-${idx}`}
                  label={`${label} · ${piece.name || "piece"} side ${String.fromCharCode(65 + idx)}`}
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Chip
                      on={false}
                      onClick={() => spinAppliance(piece.id, idx, 1)}
                      testId={`scene-appliance-spin-${piece.id}-${idx}`}
                      title="Turn this appliance 90° clockwise"
                    >
                      <ArrowsClockwise size={13} weight="bold" /> Spin 90°
                    </Chip>
                    <Chip
                      on={false}
                      onClick={() => spinAppliance(piece.id, idx, 2)}
                      testId={`scene-appliance-flip-${piece.id}-${idx}`}
                      title="Turn this appliance around (180°)"
                    >
                      Flip
                    </Chip>
                    <span
                      className="text-[10px] uppercase tracking-widest text-zinc-500"
                      data-testid={`scene-appliance-facing-${piece.id}-${idx}`}
                    >
                      faces {facing}
                    </span>
                  </div>
                </MenuRow>
              );
            })}
          </Menu>
        )}

        <Menu icon={Cube} label="Cabinets" testId="scene-menu-cabinets">
          <MenuRow label="Color">
            <div
              className="flex flex-wrap items-center gap-1"
              data-testid="scene-cabinet-colors"
            >
              {Object.entries(CAB_COLORS).map(([name, hex]) => (
                <button
                  key={name}
                  type="button"
                  title={`${name} cabinets`}
                  data-testid={`scene-cabinet-${finishSlug(name)}`}
                  onClick={() => set({ cabinet: name })}
                  className={`h-7 w-7 rounded-full border-2 transition-transform duration-150 hover:scale-110 ${
                    cabinetName === name
                      ? "border-indigo-400"
                      : "border-zinc-700"
                  }`}
                  style={{
                    background: CAB_WOOD[name]
                      ? `repeating-linear-gradient(100deg, ${hex} 0 3px, rgba(0,0,0,0.22) 3px 4px)`
                      : hex,
                  }}
                />
              ))}
            </div>
          </MenuRow>
          <MenuRow label="Show">
            <Chip
              on={scene.cabinets !== false}
              onClick={() => set({ cabinets: scene.cabinets === false })}
              testId="scene-toggle-cabinets"
            >
              Cabinets
            </Chip>
            {scene.cabinets !== false && (
              <Chip
                on={scene.uppers === true}
                onClick={() => set({ uppers: scene.uppers !== true })}
                testId="scene-toggle-uppers"
                title="Hang wall cabinets above every counter edge that sits against a wall"
              >
                Uppers
              </Chip>
            )}
          </MenuRow>
          {scene.cabinets !== false && (
            <>
              <MenuRow label="Doors">
                <div
                  className="flex items-center gap-1"
                  data-testid="scene-door-styles"
                >
                  {DOOR_STYLES.map(([key, label]) => (
                    <Chip
                      key={key}
                      on={(scene.door || "shaker") === key}
                      onClick={() => set({ door: key })}
                      testId={`scene-door-${key}`}
                      title={`${label} doors`}
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </MenuRow>
              <MenuRow label="Handles">
                <div
                  className="flex items-center gap-1"
                  data-testid="scene-hardware-options"
                >
                  {HARDWARE.map(([key, label]) => (
                    <Chip
                      key={key}
                      on={(scene.hardware || "pulls") === key}
                      onClick={() => set({ hardware: key })}
                      testId={`scene-hardware-${key}`}
                      title={`Cabinet hardware: ${label}`}
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </MenuRow>
            </>
          )}
        </Menu>

        <Menu icon={Wrench} label="Fixtures" testId="scene-menu-fixtures">
          <MenuRow label="Sinks & taps">
            <Chip
              on={scene.sinks !== false}
              onClick={() => set({ sinks: scene.sinks === false })}
              testId="scene-toggle-sinks"
            >
              Sinks
            </Chip>
          </MenuRow>
          {hasCooktop && scene.sinks !== false && (
            <MenuRow label="Cooktop">
              <div
                className="flex items-center gap-1"
                data-testid="scene-cooktop-styles"
              >
                {COOKTOPS.map(([key, label]) => (
                  <Chip
                    key={key}
                    on={cooktop === key}
                    onClick={() => set({ cooktop: key })}
                    testId={`scene-cooktop-${key}`}
                    title={`${label} electric cooktop`}
                  >
                    {label}
                  </Chip>
                ))}
              </div>
            </MenuRow>
          )}
          <MenuRow label="Appliances">
            <Chip
              on={scene.appliances !== false}
              onClick={() => set({ appliances: scene.appliances === false })}
              testId="scene-toggle-appliances"
              title="Show the fridge and range on the edges you marked in the drawing"
            >
              <Snowflake size={12} weight="bold" />
              <Fire size={12} weight="bold" />
              Fridge &amp; range
            </Chip>
          </MenuRow>
        </Menu>

        <Menu icon={House} label="Room" testId="scene-menu-room">
          <MenuRow label="Walls">
            <Chip
              on={scene.walls === true}
              onClick={() => set({ walls: scene.walls !== true })}
              testId="scene-toggle-walls"
              title="Show the walls behind the counters"
            >
              Walls
            </Chip>
          </MenuRow>
          <MenuRow label="Floor">
            <div
              className="flex items-center gap-1"
              data-testid="scene-floor-options"
            >
              {FLOORS.map(([key, label]) => (
                <Chip
                  key={key}
                  on={floor === key}
                  onClick={() => set({ floor: key })}
                  testId={`scene-floor-${key}`}
                  title={`${label} flooring`}
                >
                  {label}
                </Chip>
              ))}
            </div>
          </MenuRow>
          <MenuRow label="Lighting">
            <label
              className="flex h-8 items-center gap-2 rounded-full border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400"
              title="Room brightness"
              data-testid="scene-light-control"
            >
              <Sun size={13} weight="bold" />
              <input
                type="range"
                min="0.4"
                max="1.8"
                step="0.05"
                value={light}
                onChange={(e) => set({ light: Number(e.target.value) })}
                data-testid="scene-light-slider"
                className="h-1 w-20 cursor-pointer accent-indigo-400"
              />
              <span
                className="font-mono-data w-8 text-right text-zinc-500"
                data-testid="scene-light-value"
              >
                {Math.round(light * 100)}%
              </span>
            </label>
            <Chip
              on={false}
              onClick={() =>
                set({ mode: scene.mode === "dark" ? "light" : "dark" })
              }
              testId="scene-toggle-mode"
              title="Light / dark room"
            >
              {scene.mode === "dark" ? (
                <Sun size={13} weight="bold" />
              ) : (
                <Moon size={13} weight="bold" />
              )}
              {scene.mode === "dark" ? "Light room" : "Dark room"}
            </Chip>
          </MenuRow>
          {pieces.length > 1 && (
            <MenuRow label="Show pieces">
              <div
                className="flex flex-wrap items-center gap-1"
                data-testid="scene-piece-toggles"
              >
                {pieces.map((p, i) => (
                  <Chip
                    key={p.id}
                    on={!hidden.includes(p.id)}
                    onClick={() => togglePiece(p.id)}
                    testId={`scene-piece-${p.id}`}
                    title={`Show or hide ${p.label || `piece ${i + 1}`}`}
                  >
                    {p.label || `Piece ${i + 1}`}
                  </Chip>
                ))}
              </div>
            </MenuRow>
          )}
        </Menu>

        <div className="ml-auto flex items-center gap-1">
          <Menu icon={DotsThree} label="View" testId="scene-menu-view" align="right">
            <MenuRow label="Camera">
              <Chip
                on={false}
                onClick={() => setResetKey((k) => k + 1)}
                testId="scene-reset-view"
                title="Reset the camera"
              >
                <ArrowsClockwise size={13} weight="bold" /> Reset
              </Chip>
            </MenuRow>
            <MenuRow label="Download picture">
              <Chip
                on={false}
                onClick={() => snapshot()}
                testId="scene-snapshot"
                title="Download a picture"
              >
                <Camera size={13} weight="bold" /> Snapshot
              </Chip>
              <Chip
                on={false}
                onClick={() => snapshot(3840, "-4k")}
                testId="scene-snapshot-4k"
                title="Download a 4K (3840px wide) render"
              >
                4K
              </Chip>
            </MenuRow>
            {onPortalShown && (
              <MenuRow label="Customer portal">
                <Chip
                  on={portalShown !== false}
                  onClick={() => onPortalShown(portalShown === false)}
                  testId="scene-portal-toggle"
                  title="Let the customer spin this 3D view in their portal"
                >
                  {portalShown !== false ? (
                    <Eye size={13} weight="bold" />
                  ) : (
                    <EyeSlash size={13} weight="bold" />
                  )}
                  {portalShown !== false ? "In portal" : "Hidden from portal"}
                </Chip>
              </MenuRow>
            )}
          </Menu>
          {onHero && quote.id && (
            <>
              <button
                type="button"
                data-testid="scene-hero-save"
                onClick={saveHero}
                disabled={heroBusy || !pieces.length}
                title="Put this view on the quote document as the 'Your kitchen' picture"
                className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-3 py-1 text-[11px] font-semibold text-emerald-200 transition-colors duration-150 hover:bg-emerald-500/25 disabled:opacity-50"
              >
                <FilePlus size={13} weight="bold" />
                {heroBusy
                  ? "Saving…"
                  : hero
                    ? "Update quote picture"
                    : "Add to quote"}
              </button>
              {hero && (
                <button
                  type="button"
                  data-testid="scene-hero-remove"
                  onClick={removeHero}
                  disabled={heroBusy}
                  title="Take the picture off the quote document"
                  className="flex items-center gap-1 rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] font-semibold text-zinc-400 transition-colors duration-150 hover:border-rose-500/50 hover:text-rose-300 disabled:opacity-50"
                >
                  <Trash size={13} weight="bold" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
      )}
      <div ref={wrapRef} className="relative flex-none" style={{ height }}>
        {!pieces.length ? (
          <div
            data-testid="scene-empty"
            className="flex h-full items-center justify-center p-8 text-center text-sm text-zinc-500"
          >
            Draw at least one piece on the Draw tab and it will appear here in
            3D.
          </div>
        ) : (
          <Canvas
            shadows
            dpr={[1, 2]}
            gl={{
              preserveDrawingBuffer: true,
              antialias: true,
              powerPreference: "high-performance",
            }}
            camera={{ fov: 38, near: 1, far: 6000, position: [200, 160, 260] }}
            onPointerMissed={editable ? () => setSelected(null) : undefined}
          >
            <CameraFit box={box} resetKey={resetKey} />
            <Orbit
              target={[box.cx, 0, box.cz]}
              resetKey={resetKey}
              controlsRef={controlsRef}
            />
            <Capture api={captureRef} />
            <Scene
              key={styleKey}
              pieces={visiblePieces}
              panels={panels}
              thick={thick}
              scene={scene}
              stones={stones}
              box={box}
              cad={cad}
              cooktop={cooktop}
              selected={selected}
              editable={editable}
              seams={seams}
              onPiecePointerDown={onPiecePointerDown}
              onRotateStart={onRotateStart}
              onLiftStart={onLiftStart}
              drag={drag}
              controlsRef={controlsRef}
              onDragMove={onDragMove}
              onDragEnd={onDragEnd}
              onSpinAppliance={
                onApplyToDrawing && area
                  ? (pid, idx, turns) => {
                      spinAppliance(pid, idx, turns);
                      toast.message(turns === 2 ? "Appliance turned around" : "Appliance turned 90° — shift-click to turn it around");
                    }
                  : undefined
              }
            />
          </Canvas>
        )}
        {editable && pieces.length > 0 && (
          <ArrangeToolbar
            snapMode={snapMode}
            onSnapMode={setSnapMode}
            piece={selPiece}
            pieceName={selPiece ? nameOf(selPiece.id) : ""}
            attachName={
              selPiece?.attach?.piece_id ? nameOf(selPiece.attach.piece_id) : ""
            }
            connected={connected}
            seams={seams}
            hasOverrides={hasOverrides}
            onRotate={rotateBy}
            onSetRot={setRot}
            onFlip={flip}
            onNudge={nudge}
            onLift={liftBy}
            onSetLift={setLift}
            onResetPiece={() => selPiece && clearOver(selPiece.id)}
            onStand={(kind) => selPiece && patchOver(selPiece.id, { stand: kind })}
            onDetach={() => selPiece && patchOver(selPiece.id, { attach: null })}
            onApply={applyToDrawing}
            onResetAll={clearArea}
          />
        )}
        {!toolbar && onHero && quote.id && pieces.length > 0 && (
          <div className="absolute right-3 top-3 flex items-center gap-1.5" data-testid="c3d-hero-bar">
            <button
              type="button"
              data-testid="c3d-snapshot"
              onClick={() => snapshot()}
              title="Download this view as a picture"
              className="flex items-center rounded-full border border-zinc-700 bg-zinc-950/80 px-2.5 py-1 text-[11px] font-semibold text-zinc-300 backdrop-blur transition-colors hover:bg-zinc-800"
            >
              <Camera size={13} weight="bold" />
            </button>
            <button
              type="button"
              data-testid="c3d-hero-save"
              onClick={saveHero}
              disabled={heroBusy}
              title="Put this view on the quote as the customer's picture"
              className="flex items-center gap-1.5 rounded-full border border-emerald-400 bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white shadow-lg backdrop-blur transition-colors hover:bg-emerald-500 disabled:opacity-50"
            >
              <FilePlus size={13} weight="bold" />
              {heroBusy ? "Saving…" : hero ? "Update quote picture" : "Add to quote"}
            </button>
            {hero && (
              <button
                type="button"
                data-testid="c3d-hero-remove"
                onClick={removeHero}
                disabled={heroBusy}
                title="Remove this picture from the quote"
                className="flex items-center rounded-full border border-zinc-700 bg-zinc-950/80 px-2 py-1 text-[11px] text-zinc-400 backdrop-blur transition-colors hover:text-rose-300 disabled:opacity-50"
              >
                <Trash size={13} weight="bold" />
              </button>
            )}
          </div>
        )}
        <div className="pointer-events-none absolute bottom-3 left-3 rounded-full border border-zinc-800 bg-zinc-950/80 px-3 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 backdrop-blur">
          {editable ? "Click a piece · drag it to move · " : ""}
          {editable && appliances.length > 0 ? "click an appliance to spin it · " : ""}
          Drag to orbit · wheel to zoom · right-drag to pan ·{" "}
          {quote.thickness || "3cm"} stone
        </div>
      </div>
    </div>
  );
};

export default Countertop3D;
